import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Shield, CheckCircle, Users, FileText, Scale, Heart, Zap, Handshake, Calculator, MessageCircle, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const UGL_LOGO = "/ugl-logo.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VANTAGGI = [
  { icon: Calculator, title: "CAF Gratuito", desc: "Assistenza fiscale completa: 730, ISEE, RED e bonus" },
  { icon: Shield, title: "Patronato", desc: "Pratiche previdenziali, pensioni e disoccupazione" },
  { icon: Scale, title: "Assistenza Legale", desc: "Consulenza e tutela in ambito lavorativo" },
  { icon: Heart, title: "Medico Legale", desc: "Perizie e visite per invalidità e infortuni" },
  { icon: Zap, title: "Tariffe Energia", desc: "Sconti esclusivi su contratti luce e gas" },
  { icon: Handshake, title: "Convenzioni", desc: "Agevolazioni con commercianti e servizi" },
];

export default function TesseramentoPage() {
  const [form, setForm] = useState({ nome: "", cognome: "", codice_fiscale: "", email: "", telefono: "", messaggio: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.cognome || !form.codice_fiscale || !form.telefono) {
      toast.error("Compila i campi obbligatori");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/tesseramento`, form);
      toast.success("Richiesta inviata! Ti contatteremo presto.");
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'invio. Riprova.");
    }
    setSending(false);
  };

  return (
    <div className="min-h-screen section-dark text-white relative overflow-hidden" role="main" aria-label="Pagina tesseramento">
      <div className="section-grid-bg" />

      {/* Header */}
      <header className="glass-header bg-[#001830]/90 border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 sm:px-12 flex items-center justify-between h-14">
          <Link to="/" className="flex items-center gap-3">
            <img src={UGL_LOGO} alt="UGL" className="w-8 h-8 object-contain" />
            <span className="font-semibold text-white text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>UGL Lariano</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" size="sm" className="text-white/60 hover:text-white hover:bg-white/5">
              <ArrowLeft className="w-4 h-4 mr-1" /> Torna alla Home
            </Button>
          </Link>
        </div>
      </header>

      <div className="relative z-10 max-w-6xl mx-auto px-6 sm:px-12 py-16">
        {/* Hero */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="vintage-seal w-14 h-14 rounded-full border-2 border-[#C9A84C] flex items-center justify-center">
              <Users className="w-6 h-6 text-[#C9A84C]" />
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Diventa Iscritto <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00A0D1] to-[#C9A84C]">UGL Lariano</span>
          </h1>
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-px w-12 bg-[#C9A84C]/40" />
            <div className="w-2 h-2 rotate-45 border border-[#C9A84C]/40" />
            <div className="h-px w-12 bg-[#C9A84C]/40" />
          </div>
          <p className="text-base text-white/50 max-w-xl mx-auto" style={{ fontFamily: "'Georgia', serif" }}>
            Entra a far parte della famiglia UGL. Goditi tutti i vantaggi riservati agli iscritti 
            e contribuisci a costruire una comunità di lavoratori più forte.
          </p>
        </div>

        {/* Vantaggi Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-16">
          {VANTAGGI.map((v, i) => (
            <div key={i} className="glass-card rounded-lg border border-white/10 p-5 flex items-start gap-4 animate-slide-up" style={{ opacity: 0, animationFillMode: 'forwards', animationDelay: `${i * 0.08}s` }}>
              <div className="w-10 h-10 rounded-md bg-[#C9A84C]/10 flex items-center justify-center flex-shrink-0">
                <v.icon className="w-5 h-5 text-[#C9A84C]" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{v.title}</h3>
                <p className="text-xs text-white/40">{v.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Form */}
        <div className="max-w-2xl mx-auto">
          <div className="glass-card rounded-xl border border-[#C9A84C]/20 p-8" data-testid="tesseramento-form-card">
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Richiedi il Tesseramento
              </h2>
              <p className="text-xs text-white/40">Compila il modulo e ti contatteremo per completare l'iscrizione</p>
            </div>

            {sent ? (
              <div className="text-center py-12" data-testid="tesseramento-success">
                <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-emerald-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Richiesta Inviata!</h3>
                <p className="text-sm text-white/50 mb-6">Ti contatteremo al piu presto per completare il tesseramento.</p>
                <div className="flex items-center justify-center gap-4">
                  <a href="https://wa.me/393514134225?text=Salve%2C%20ho%20compilato%20il%20modulo%20di%20tesseramento%20UGL%20Lariano" target="_blank" rel="noopener noreferrer">
                    <Button className="bg-[#25D366] text-white hover:bg-[#25D366]/90">
                      <MessageCircle className="w-4 h-4 mr-2" /> Contattaci su WhatsApp
                    </Button>
                  </a>
                  <Link to="/">
                    <Button variant="outline" className="border-white/20 text-white/60 hover:bg-white/5">
                      Torna alla Home
                    </Button>
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-white/60">Nome *</Label>
                    <Input data-testid="tess-nome" value={form.nome} onChange={e => setForm(p => ({ ...p, nome: e.target.value }))}
                      className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/20 focus:border-[#00A0D1]" placeholder="Mario" />
                  </div>
                  <div>
                    <Label className="text-xs text-white/60">Cognome *</Label>
                    <Input data-testid="tess-cognome" value={form.cognome} onChange={e => setForm(p => ({ ...p, cognome: e.target.value }))}
                      className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/20 focus:border-[#00A0D1]" placeholder="Rossi" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-white/60">Codice Fiscale *</Label>
                  <Input data-testid="tess-cf" value={form.codice_fiscale} onChange={e => setForm(p => ({ ...p, codice_fiscale: e.target.value.toUpperCase() }))}
                    className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/20 uppercase tracking-wider focus:border-[#00A0D1]" placeholder="RSSMRA85M01H501Z" maxLength={16} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-white/60">Email</Label>
                    <Input data-testid="tess-email" type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                      className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/20 focus:border-[#00A0D1]" placeholder="mario@email.it" />
                  </div>
                  <div>
                    <Label className="text-xs text-white/60">Telefono *</Label>
                    <Input data-testid="tess-tel" value={form.telefono} onChange={e => setForm(p => ({ ...p, telefono: e.target.value }))}
                      className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/20 focus:border-[#00A0D1]" placeholder="351 000 0000" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-white/60">Messaggio (opzionale)</Label>
                  <Textarea data-testid="tess-messaggio" value={form.messaggio} onChange={e => setForm(p => ({ ...p, messaggio: e.target.value }))}
                    className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/20 focus:border-[#00A0D1]" placeholder="Scrivi qui eventuali note..." rows={3} />
                </div>
                <Button type="submit" data-testid="tess-submit" disabled={sending}
                  className="w-full bg-gradient-to-r from-[#C9A84C] to-[#B8963C] text-[#001830] hover:from-[#C9A84C]/90 hover:to-[#B8963C]/90 h-12 font-semibold text-sm rounded-md">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  {sending ? "Invio in corso..." : "Invia Richiesta di Tesseramento"}
                </Button>
                <p className="text-[10px] text-white/25 text-center">
                  Inviando il modulo accetti la nostra <Link to="/privacy" className="text-[#00A0D1]/50 hover:text-[#00A0D1]">Privacy Policy</Link>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* WhatsApp */}
      <a href="https://wa.me/393514134225" target="_blank" rel="noopener noreferrer"
        className="whatsapp-btn fixed bottom-6 right-6 z-[90] w-14 h-14 rounded-full bg-[#25D366] flex items-center justify-center shadow-lg shadow-[#25D366]/30 hover:scale-110 transition-all duration-300">
        <MessageCircle className="w-6 h-6 text-white" />
      </a>
    </div>
  );
}
